import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { vehicleFormHooks } from 'app-components/flux/vehicle/vehicleForm';
import type { VehicleFormState, VehicleInputMode } from 'app-components/flux/vehicle/vehicleTypes';
import { businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import QButton from 'components/QButton';
import React from 'react';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';
import {
  AutoUpdateMilesToggle,
  BusinessPurposeSelect,
  MakeInput,
  MilesInput,
  ModelInput,
  NameInput,
  TrimInput,
  VinInput,
  YearInput,
  ZipCodeInput,
} from '../VehicleFields';
import { UncheckedIcon } from '../utils';

const useStyles = makeStyles()((theme) => ({
  mainTitle: {
    marginBottom: theme.spacing(2),
  },
  inputModeOptions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,

    '& >:first-child': {
      borderRight: `1px solid ${theme.palette.border.light}`,
    },
  },
  inputOption: {
    flex: 1,
    margin: 0,
    padding: theme.spacing(2, 3, 2, 3),
  },
  selectedInputOption: {
    color: theme.palette.active.main,
  },
  radioRoot: {
    height: 20,
    width: 20,
    padding: 0,
    marginRight: theme.spacing(1.25),

    '&.Mui-checked': {
      color: theme.palette.background.paper,
      backgroundColor: theme.palette.active.main,
      '& svg': {
        padding: theme.spacing(0.5),
      },
    },
  },
  uncheckedIcon: {
    color: theme.palette.border.dark,
  },
  makeModelInputContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(0.5, 3),
    padding: theme.spacing(3, 0, 3, 0),
  },
  autoMileageContainer: {
    gridColumn: 'span 2',
  },
  vinInputContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(3),
    gap: theme.spacing(0.5),
  },
  milesZipcodeContainer: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  editDetailsButton: {
    padding: 0,
    textTransform: 'none',
  },
  nameBusinessRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: theme.spacing(2),
  },
  nameBusinessField: {
    flex: 1,
    minWidth: 0,
  },
  businessField: {
    flex: 0.5,
  },
}));

const BusinessSelect = () => {
  const { classes } = useStyles();

  const businessEnabled: boolean = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const showBusinessSelect = useAppSelector(
    (state) => businessSelectors.getBusinessList(state)?.size > 0 && businessEnabled,
  );

  if (!showBusinessSelect) {
    return null;
  }

  return (
    <Box className={classes.businessField}>
      <BusinessPurposeSelect />
    </Box>
  );
};

const MakeModelSelection = ({
  readOnly = false,
  originalValues,
}: {
  readOnly?: boolean;
  originalValues?: VehicleFormState;
}) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.makeModelInputContainer}>
      <YearInput readOnly={readOnly} readOnlyValue={originalValues?.year} />
      <MakeInput readOnly={readOnly} readOnlyValue={originalValues?.make?.makeName} />
      <ModelInput readOnly={readOnly} readOnlyValue={originalValues?.model?.modelName} />
      <TrimInput readOnly={readOnly} readOnlyValue={originalValues?.trim?.trimName} />
      <MilesInput readOnly={readOnly} readOnlyValue={originalValues?.miles} />
      <ZipCodeInput readOnly={readOnly} readOnlyValue={originalValues?.zipCode} />
      <AutoUpdateMilesToggle className={classes.autoMileageContainer} />
    </Box>
  );
};

const VinInputSelection = () => {
  const { classes } = useStyles();

  return (
    <Box className={classes.vinInputContainer}>
      <VinInput />
      <Box className={classes.milesZipcodeContainer}>
        <MilesInput />
        <ZipCodeInput />
      </Box>
      <AutoUpdateMilesToggle />
    </Box>
  );
};

const ReadOnlyDetails = ({
  onEditDetails,
  originalValues,
}: {
  onEditDetails: () => void;
  originalValues?: VehicleFormState;
}) => {
  const { classes } = useStyles();

  const { value: linkTo } = vehicleFormHooks.useField('linkTo');

  return (
    <Box>
      <Box className={classes.titleRow}>
        <Typography variant='headlineRegular' className={classes.mainTitle}>
          Automobile details
        </Typography>
        <QButton
          className={classes.editDetailsButton}
          variant='text'
          disableRipple
          endIcon={<ArrowForwardIcon />}
          onClick={onEditDetails}
        >
          Edit Details
        </QButton>
      </Box>
      <Box className={classes.nameBusinessRow}>
        <Box className={classes.nameBusinessField}>
          <NameInput linkTo={linkTo} />
        </Box>
        <BusinessSelect />
      </Box>
      <MakeModelSelection readOnly={true} originalValues={originalValues} />
    </Box>
  );
};

const EditableDetails = () => {
  const { classes } = useStyles();

  const { value: inputMode } = vehicleFormHooks.useField('inputMode');

  const manager = vehicleFormHooks.useManager();

  const switchInputMode = (mode: VehicleInputMode) => {
    manager.reinit(manager.runSelector(({ state }) => ({ ...state, inputMode: mode })));
  };

  return (
    <>
      <Typography variant='headlineRegular' className={classes.mainTitle}>
        Let&apos;s find the value of your car
      </Typography>
      <FormControl fullWidth>
        <RadioGroup
          className={classes.inputModeOptions}
          value={inputMode}
          onChange={(e) => switchInputMode(e.target.value as VehicleInputMode)}
        >
          <FormControlLabel
            value='makeModel'
            className={`${classes.inputOption} ${inputMode === 'makeModel' ? classes.selectedInputOption : ''}`}
            control={
              <Radio
                icon={<UncheckedIcon className={classes.uncheckedIcon} />}
                checkedIcon={<CheckIcon />}
                className={classes.radioRoot}
              />
            }
            label={'Use Make/Model'}
          />
          <FormControlLabel
            value='vin'
            className={`${classes.inputOption} ${inputMode === 'vin' ? classes.selectedInputOption : ''}`}
            control={
              <Radio
                icon={<UncheckedIcon className={classes.uncheckedIcon} />}
                checkedIcon={<CheckIcon />}
                className={classes.radioRoot}
              />
            }
            label={'Use VIN'}
          />
        </RadioGroup>
      </FormControl>
      {inputMode === 'makeModel' ? <MakeModelSelection /> : <VinInputSelection />}
    </>
  );
};

type EditableMode = {
  readOnly?: false;
};

type ReadOnlyMode = {
  readOnly: true;
  onEditDetails: () => void;
  originalValues?: VehicleFormState;
};

type DetailsStepProps = EditableMode | ReadOnlyMode;

export const DetailsStep = (props: DetailsStepProps) => {
  const originalValues = props.readOnly ? props.originalValues : undefined;

  return (
    <Box>
      <Box>
        {props.readOnly ? (
          <ReadOnlyDetails onEditDetails={props.onEditDetails} originalValues={originalValues} />
        ) : (
          <EditableDetails />
        )}
      </Box>
    </Box>
  );
};
